/**
 * Narrow subscription to the React Query `user-preferences` cache.
 *
 * Components that only read a subset of preferences (e.g. <Message />)
 * use this instead of `usePreferences()` so they re-render only when
 * their selected slice changes — a theme toggle no longer cascades
 * through every visible message.
 *
 * See spec F-5 (docs/superpowers/specs/2026-05-18-perf-review-design.md).
 */

import { useSyncExternalStore, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_PREFERENCES,
  type UserPreferences,
} from '@/types/preferences.types';

const PREFERENCES_QUERY_KEY = ['user-preferences'] as const;

export function usePreferencesSelector<T>(
  selector: (prefs: UserPreferences) => T
): T {
  const queryClient = useQueryClient();
  // Re-read selector through a ref so callers can pass an inline arrow
  // without churning the subscribe identity on every render.
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const subscribe = (onChange: () => void): (() => void) => {
    const unsub = queryClient.getQueryCache().subscribe((event) => {
      // Filter to the prefs query only.
      const key = event?.query?.queryKey;
      if (
        Array.isArray(key) &&
        key.length === PREFERENCES_QUERY_KEY.length &&
        key[0] === PREFERENCES_QUERY_KEY[0]
      ) {
        onChange();
      }
    });
    return unsub;
  };

  // Cache the last selected slice so useSyncExternalStore's identity
  // check skips re-renders when the new slice is `===` to the old one.
  const lastSelected = useRef<{ slice: T; source: UserPreferences } | null>(
    null
  );

  const getSnapshot = (): T => {
    const data =
      queryClient.getQueryData<UserPreferences>(PREFERENCES_QUERY_KEY) ??
      DEFAULT_PREFERENCES;

    if (lastSelected.current && lastSelected.current.source === data) {
      return lastSelected.current.slice;
    }
    const slice = selectorRef.current(data);
    if (
      lastSelected.current &&
      Object.is(lastSelected.current.slice, slice)
    ) {
      // Same value (e.g. selector returned `true` both times) — keep
      // the identity stable so the consumer sees no spurious re-render.
      lastSelected.current = {
        slice: lastSelected.current.slice,
        source: data,
      };
      return lastSelected.current.slice;
    }
    lastSelected.current = { slice, source: data };
    return slice;
  };

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
